/**
 * types/needs.ts
 *
 * Type definitions for the needs-based form creation paradigm.
 *
 * Updated Feb 2026: Replaced condition_catalog system with intra-form
 *   conditional logic. condition_source_field_id references another NeedItem
 *   in the same form (single_choice or multiple_choice), and condition_values
 *   lists which answer values trigger display.
 * Updated Feb 2026: Removed selected_subtypes and quantity from NeedItem.
 *   Subtype and quantity information is now expressed in example_description.
 * Updated Mar 2026: Replaced single condition_source_field_id + condition_values
 *   with conditions: FieldCondition[] | null, enabling OR-logic across up to 3
 *   different source fields.
 * Updated Apr 2026: Added condition_operator ('or' | 'and') to NeedItem, enabling
 *   AND-logic between multiple conditions on a single field.
 * Updated Apr 2026: Added optional `name` field to preserve the Intitulé column
 *   from SmartTable imports, displayed as an admin-facing chip in NeedItem.tsx.
 * Updated Apr 2026: Added `is_system` boolean flag for system-managed fields
 *   (e.g., the mandatory email question). System fields are locked in the form
 *   builder: non-deletable, non-movable, type-locked, always at position 0.
 * Updated Jun 2026: Removed catalog fields (document_catalog_id, catalog_item,
 *   information_catalog_id, information_catalog_item, auto_classified,
 *   original_input). The field label is now the direct source of truth.
 * Updated Jun 2026: Added short_label optional field — a short admin-facing label
 *   shown in the client detail view instead of the full question (label).
 *   Display rule everywhere in admin UI: short_label ?? label.
 *   The client-facing form always shows label unchanged.
 */

import type { FieldType } from './database';

/**
 * FieldCondition - A single condition rule for conditional field display.
 * Within a condition, the field is shown if the source field's answer matches
 * any of the listed values.
 * Multiple conditions are combined using the NeedItem.condition_operator.
 */
export interface FieldCondition {
  source_field_id: string;
  values: string[];
}

/**
 * NeedItem - Unified representation of a need (document or information field)
 * This is the core type used during form creation before saving to database.
 */
export interface NeedItem {
  id: string;
  type: FieldType;
  name?: string;
  label: string;
  short_label?: string | null;
  description: string | null;
  is_required: boolean;
  position: number;

  // System-managed field (e.g., the mandatory email question).
  // When true: non-deletable, non-movable, type-locked in the form builder.
  is_system?: boolean;

  // For choice fields (single_choice, multiple_choice)
  options?: string[];

  // Conditional display: array of condition rules (max 3).
  // condition_operator defines how multiple rules are combined:
  //   'or'  → visible if AT LEAST ONE rule is satisfied (default)
  //   'and' → visible only if ALL rules are satisfied simultaneously
  conditions: FieldCondition[] | null;
  condition_operator?: 'or' | 'and';

  // Logical grouping for client-facing experience
  section: string | null;
}

/**
 * GroupedNeeds - Needs organized by section
 */
export interface GroupedNeeds {
  [section: string]: NeedItem[];
}

/**
 * OrderedNeed - A need with final position and presentation details
 */
export interface OrderedNeed extends NeedItem {
  final_position: number;
  is_first_in_section: boolean;
  section_label: string;
  section_icon?: string;
}

/**
 * Section metadata for organizing needs
 */
export interface SectionMetadata {
  key: string;
  label: string;
  icon: string;
  order: number;
  description?: string;
  conversational_transition: string;
}

/**
 * Predefined section order for optimal client experience
 */
export const SECTION_ORDER: Record<string, number> = {
  screening: 0,
  identite: 1,
  revenus: 2,
  logement: 3,
  financier: 4,
  famille: 5,
  etudes: 6,
  informations: 7,
  autres: 8,
};

/**
 * Section labels and metadata
 */
export const SECTION_METADATA: Record<string, SectionMetadata> = {
  screening: {
    key: 'screening',
    label: 'Questions préliminaires',
    icon: 'clipboard-check',
    order: 0,
    description: 'Quelques questions rapides pour personnaliser votre dossier',
    conversational_transition: 'Quelques questions rapides pour mieux vous connaître.',
  },
  identite: {
    key: 'identite',
    label: 'Identité',
    icon: 'shield',
    order: 1,
    description: "Vos documents d'identité",
    conversational_transition: "Commençons par vos documents d'identité.",
  },
  revenus: {
    key: 'revenus',
    label: 'Revenus',
    icon: 'trending-up',
    order: 2,
    description: 'Vos justificatifs de revenus',
    conversational_transition: 'Passons maintenant à vos justificatifs de revenus.',
  },
  logement: {
    key: 'logement',
    label: 'Logement',
    icon: 'home',
    order: 3,
    description: 'Vos documents de logement',
    conversational_transition: 'Parlons maintenant de votre logement.',
  },
  financier: {
    key: 'financier',
    label: 'Informations financières',
    icon: 'wallet',
    order: 4,
    description: 'Vos documents financiers',
    conversational_transition: 'Place maintenant à vos informations financières.',
  },
  famille: {
    key: 'famille',
    label: 'Famille',
    icon: 'users',
    order: 5,
    description: 'Documents relatifs à votre situation familiale',
    conversational_transition: 'Quelques informations sur votre situation familiale.',
  },
  etudes: {
    key: 'etudes',
    label: 'Études et formation',
    icon: 'graduation-cap',
    order: 6,
    description: 'Vos diplômes et attestations',
    conversational_transition: 'Passons maintenant à vos études et formations.',
  },
  informations: {
    key: 'informations',
    label: 'Informations complémentaires',
    icon: 'file-text',
    order: 7,
    description: 'Quelques informations supplémentaires',
    conversational_transition: 'Pour finir, quelques informations complémentaires.',
  },
  autres: {
    key: 'autres',
    label: 'Autres documents',
    icon: 'folder',
    order: 8,
    description: 'Documents divers',
    conversational_transition: 'Enfin, quelques documents supplémentaires.',
  },
};
